// Sheets export "share with" rules (ORP-25238), pure.
import type { ReportSpec } from '../models/report.types'

/** The backend caps `share_with` at 10 distinct addresses (`SHEET_SHARE_TOO_MANY_RECIPIENTS`). */
export const SHEET_SHARE_MAX_RECIPIENTS = 10

/** `share_failures[].reason` values the backend's `ShareFailureReason` produces. */
const SHARE_FAILURE_REASONS = new Set([
  'permission_denied',
  'auth_required',
  'scope_missing',
  'invalid_request',
  'rate_limited',
  'unavailable',
])

/**
 * `share_with` is read only by the main workspace controller route; the composition,
 * precalculated-product and multi-workspace routes ignore it, so the picker is not offered there.
 */
export function sheetShareSupported(input: {
  spec: Pick<ReportSpec, 'config_id' | 'partition_name'> | null
  workspaceIds: readonly (number | string)[]
  productPrecalculated: boolean
}): boolean {
  if (!input.spec || input.spec.config_id !== undefined || input.workspaceIds.length > 0) {
    return false
  }
  return !(input.productPrecalculated && input.spec.partition_name === 'metrics_product')
}

/** Distinct, non-empty addresses off the sharing list; anything else is dropped. */
export function normalizeShareOptions(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return [
    ...new Set(value.filter((item): item is string => typeof item === 'string' && item !== '')),
  ]
}

/** i18n key segment for a failure reason; unknown reasons read as `unknown`. */
export function shareFailureKey(reason: string): string {
  if (!SHARE_FAILURE_REASONS.has(reason)) return 'unknown'
  return reason.replace(/_([a-z])/gu, (_, character: string) => character.toUpperCase())
}

/** Keep the picker inside the allow-list and the backend's cap. */
export function clampShareSelection(
  selected: readonly string[],
  options: readonly string[],
): string[] {
  const allowed = new Set(options)
  return [...new Set(selected)]
    .filter((email) => allowed.has(email))
    .slice(0, SHEET_SHARE_MAX_RECIPIENTS)
}
